import { Box } from "@mui/material";
import {
  type ColumnOrderState,
  type ColumnSizingState,
  getCoreRowModel,
  type RowSelectionState,
  useReactTable,
  type VisibilityState,
} from "@tanstack/react-table";
import React, { useCallback, useContext, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Timings } from "@storyboard/web-logger/timings/Timings";
import { ampli } from "@/config";
import { actions, TelemetryContext } from "@/models/TelemetryProvider";
import type { DriverRow, TagDefinition } from "@/types/driver";
import DataTable, { buildCheckboxColumn } from "../DataTable";
import { COLUMN_LABELS } from "./ColumnVisibilityDropdown";
import { buildDriverColumns, type ColumnFilterOptions } from "./columns";
import DriverContextMenu, { driversHaveContextMenuActions } from "./DriverContextMenu";

function NoRowsOverlay() {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        bgcolor: "rgba(63, 63, 70, 0.5)",
        color: "rgb(212, 212, 216)",
      }}
    >
      No Results
    </Box>
  );
}

export default React.memo(function DriverDataGrid({
  drivers,
  activeMembershipId,
  workspaceId,
  myWorkspaceMembershipId,
  columnVisibility,
  onColumnVisibilityChange,
  columnOrder,
  onColumnOrderChange,
  columnSizing,
  onColumnSizingChange,
  rowSelection,
  onRowSelectionChange,
  getColumnExtra,
  isFilterActive,
  getActiveFilterValues,
  showUnreadOnly,
  toggleUnreadOnly,
  tags,
  onRowClick,
}: {
  drivers: DriverRow[];
  activeMembershipId: string | null;
  workspaceId: string;
  myWorkspaceMembershipId: string;
  columnVisibility: VisibilityState;
  onColumnVisibilityChange: (updater: VisibilityState | ((prev: VisibilityState) => VisibilityState)) => void;
  columnOrder: ColumnOrderState;
  onColumnOrderChange: (updater: ColumnOrderState | ((prev: ColumnOrderState) => ColumnOrderState)) => void;
  columnSizing: ColumnSizingState;
  onColumnSizingChange: (updater: ColumnSizingState | ((prev: ColumnSizingState) => ColumnSizingState)) => void;
  rowSelection: RowSelectionState;
  onRowSelectionChange: (updater: RowSelectionState | ((prev: RowSelectionState) => RowSelectionState)) => void;
  getColumnExtra: ColumnFilterOptions["getColumnExtra"];
  isFilterActive: ColumnFilterOptions["isFilterActive"];
  getActiveFilterValues: ColumnFilterOptions["getActiveFilterValues"];
  showUnreadOnly: boolean;
  toggleUnreadOnly: () => void;
  tags: TagDefinition[];
  onRowClick?: () => void;
}) {
  const navigate = useNavigate();
  const { trackAction } = useContext(TelemetryContext);

  const [contextMenuPosition, setContextMenuPosition] = useState<{ top: number; left: number } | undefined>();
  const [contextMenuTargets, setContextMenuTargets] = useState<DriverRow[]>([]);

  const getColumnExtraRef = useRef(getColumnExtra);
  getColumnExtraRef.current = getColumnExtra;
  const stableGetColumnExtra = useCallback((key: string) => getColumnExtraRef.current(key), []);

  const isFilterActiveRef = useRef(isFilterActive);
  isFilterActiveRef.current = isFilterActive;
  const stableIsFilterActive = useCallback((key: string) => isFilterActiveRef.current(key), []);

  const getActiveFilterValuesRef = useRef(getActiveFilterValues);
  getActiveFilterValuesRef.current = getActiveFilterValues;
  const stableGetActiveFilterValues = useCallback((key: string) => getActiveFilterValuesRef.current(key), []);

  const shiftSelectRef = useRef<string | null>(null);

  const columns = useMemo(
    () => [
      buildCheckboxColumn<DriverRow>({
        shiftSelectRef,
        onBulkSelect: () => ampli.bulkSelectDrivers(),
      }),
      ...buildDriverColumns(
        {
          getColumnExtra: stableGetColumnExtra,
          isFilterActive: stableIsFilterActive,
          getActiveFilterValues: stableGetActiveFilterValues,
          showUnreadOnly,
          toggleUnreadOnly,
        },
        tags,
      ),
    ],
    [stableGetColumnExtra, stableIsFilterActive, stableGetActiveFilterValues, showUnreadOnly, toggleUnreadOnly, tags],
  );

  const table = useReactTable({
    data: drivers,
    columns,
    state: { columnVisibility, columnOrder, columnSizing, rowSelection },
    onColumnVisibilityChange,
    onColumnOrderChange,
    onColumnSizingChange,
    onRowSelectionChange,
    getRowId: (row) => row.workspaceMembershipId,
    getCoreRowModel: getCoreRowModel(),
    enableSorting: false,
    enableColumnResizing: true,
    columnResizeMode: "onChange",
  });

  const handleRowClick = useCallback(
    (row: DriverRow) => {
      if (row.feedId == null) return;
      Timings.createEvent("driver-row-click", {
        properties: { feedId: row.feedId, driverName: row.name },
      });
      trackAction(actions.feedNavigate(row.feedId));
      navigate(`/workspaces/${row.workspaceId}/drivers/${row.workspaceMembershipId}/channel/${row.feedId}`);
      onRowClick?.();
    },
    [navigate, onRowClick, trackAction],
  );

  const handleRowContextMenu = useCallback(
    (row: DriverRow, event: React.MouseEvent) => {
      const targets = rowSelection[row.workspaceMembershipId]
        ? drivers.filter((d) => rowSelection[d.workspaceMembershipId])
        : [row];
      if (!driversHaveContextMenuActions(targets)) return;

      const position = { top: event.clientY - 6, left: event.clientX + 2 };
      setContextMenuTargets(targets);
      setContextMenuPosition(position);
    },
    [rowSelection, drivers],
  );

  const handleContextMenuClose = useCallback(() => {
    setContextMenuPosition(undefined);
    setContextMenuTargets([]);
  }, []);

  const resolveColumnName = useCallback(
    (columnId: string): string => {
      if (columnId.startsWith("tag:")) {
        const tagId = columnId.slice(4);
        return tags.find((t) => t.id === tagId)?.label ?? columnId;
      }
      return COLUMN_LABELS[columnId] ?? columnId;
    },
    [tags],
  );

  const handleHideColumn = useCallback(
    (columnId: string) => {
      ampli.hideColumn({ columnName: resolveColumnName(columnId) });
    },
    [resolveColumnName],
  );

  const getRowClassName = useCallback(
    (row: DriverRow) => {
      if (row.workspaceMembershipId === activeMembershipId) return "driver-row-active";
      if (row.feedPrefId) return "driver-row-notified";
      return "";
    },
    [activeMembershipId],
  );

  const getRowCursor = useCallback((row: DriverRow) => (row.feedId == null ? "default" : "pointer"), []);

  return (
    <>
      <DataTable
        table={table}
        onRowClick={handleRowClick}
        onRowContextMenu={handleRowContextMenu}
        getRowClassName={getRowClassName}
        getRowCursor={getRowCursor}
        noRowsContent={<NoRowsOverlay />}
        pagination={false}
        virtualize
        enableColumnHiding
        onHideColumn={handleHideColumn}
        rowHeight={32}
        headerHeight={32}
        sx={{
          "& .MuiTableContainer-root": {
            border: "none",
          },
          "& .MuiTableRow-root": {
            borderLeft: "4px solid transparent",
          },
          "& .MuiTableCell-root": {
            py: 0.25,
            px: 1,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          },
          "& .driver-row-active": {
            bgcolor: "rgb(82, 82, 91)",
            "&:hover": {
              bgcolor: "rgb(82, 82, 91)",
            },
          },
          "& .driver-row-notified": {
            borderLeftColor: "var(--color-brand-primary-500)",
          },
          "& .MuiTableHead-root .MuiTableCell-root": {
            bgcolor: "rgb(39, 39, 42)",
          },
          "& .MuiTableBody-root .frozen-column": {
            bgcolor: "rgb(39, 39, 42)",
          },
          "& .MuiTableRow-root:hover:not(.driver-row-active) .frozen-column": {
            bgcolor: "rgb(56, 56, 59)",
          },
          "& .driver-row-active .frozen-column": {
            bgcolor: "rgb(82, 82, 91)",
          },
        }}
      />
      <DriverContextMenu
        anchorPosition={contextMenuPosition}
        drivers={contextMenuTargets}
        workspaceId={workspaceId}
        myWorkspaceMembershipId={myWorkspaceMembershipId}
        onClose={handleContextMenuClose}
      />
    </>
  );
});
